import React, { useState, useEffect } from 'react';
import {
  Drawer, TextField, Button, Box, Typography, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, IconButton, Divider, Chip
} from '@mui/material';
import { Close as CloseIcon, VideoCall as VideoCallIcon, Edit as EditIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import { validateMeetingData } from '../../../../service/manage/zoom_meeting/zoom_meeting.service';
import './CreateMeeting.css';

const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1 hour 30 minutes' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
  { value: 360, label: '6 hours' },
  { value: 480, label: '8 hours' },
  { value: 720, label: '12 hours' },
  { value: 1440, label: '24 hours (max)' }
];

const initialData = () => ({
  topic: '',
  description: '', // Added description field
  startdatetime: '',
  duration: 15,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
});

const tzDisplay = tz => {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(now);
    return `${tz} (${parts.find(p => p.type === 'timeZoneName')?.value})`;
  } catch { return tz; }
};

const validateLength = (value, maxLength) => {
  return value && value.length > maxLength;
};

export default function CreateZoomMeeting({ open, onClose, onCreate, onUpdate, isProcessing, editingMeeting }) {
  const [formData, setFormData] = useState(initialData());
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const isEdit = Boolean(editingMeeting);

  useEffect(() => {
  if (!open) return;

  const formatLocalDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  setFormData(isEdit ? {
    topic: editingMeeting.topic || '',
    description: editingMeeting.description || '',
    startdatetime: editingMeeting.startdatetime ? formatLocalDateTime(editingMeeting.startdatetime) : '',
    duration: editingMeeting.duration || 40,
    timezone: editingMeeting.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  } : initialData());

  setErrors({});
  setSubmitError('');
}, [open, isEdit, editingMeeting]);


  const changeHandler = (field, value) => {
    // Length validation
    const newErrors = { ...errors };
    if (field === 'topic' && validateLength(value, 128)) {
      newErrors.topic = 'Topic must be 128 characters or less';
    } else if (field === 'description' && validateLength(value, 255)) {
      newErrors.description = 'Description must be 255 characters or less';
    } else {
      delete newErrors[field];
    }

    setFormData(prev => ({ ...prev, [field]: value, ...(field === 'startdatetime' && { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }) }));
    setErrors(newErrors);
    if (submitError) setSubmitError('');
  };

  const minDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 15);
    return now.toISOString().slice(0, 16);
  };

  const submitHandler = async e => {
    e.preventDefault();
    
    // Check length validations before submitting
    const lengthErrors = {};
    if (validateLength(formData.topic, 128)) {
      lengthErrors.topic = 'Topic must be 128 characters or less';
    }
    if (validateLength(formData.description, 255)) {
      lengthErrors.description = 'Description must be 255 characters or less';
    }
    
    if (Object.keys(lengthErrors).length > 0) {
      setErrors(lengthErrors);
      return;
    }

    const validation = validateMeetingData(formData);
    if (!validation.isValid) return setErrors(validation.errors);
    try {
      isEdit ? await onUpdate(editingMeeting.meeting_id, formData) : await onCreate(formData);
      resetForm();
    } catch (err) {
      setSubmitError(err.message || `Failed to ${isEdit ? 'update' : 'create'} meeting`);
    }
  };

  const resetForm = () => { setFormData(initialData()); setErrors({}); setSubmitError(''); };
  const closeHandler = () => { if (!isProcessing) { resetForm(); onClose(); } };

  return (
    <Drawer 
      anchor="right" 
      open={open} 
      onClose={closeHandler} 
      className="create-meeting-drawer"
      PaperProps={{ 
        sx: { width: { xs: '100%', sm: 480 } }, 
        className: 'create-meeting-paper' 
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box 
          className="create-meeting-header"
          sx={{ 
            p: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            color: 'white' 
          }}
        >
          <Box className="meeting-header-container">
  <Box className="meeting-header-icon">
    {isEdit ? <EditIcon fontSize="inherit" /> : <VideoCallIcon fontSize="inherit" />}
  </Box>
  <Box className="meeting-header-text">
    <Typography className="meeting-header-title">
      {isEdit ? 'Edit Zoom Meetingf' : 'Schedule New Meeting'}
    </Typography>
    <Typography className="meeting-header-subtitle">
      {isEdit ? 'Update meeting details' : 'Create a new Zoom meeting'}
    </Typography>
  </Box>
</Box>

          <IconButton 
            onClick={closeHandler} 
            disabled={isProcessing} 
            className="create-meeting-close-btn"
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Form */}
        <Box sx={{ flex: 1, p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box 
            component="form" 
            onSubmit={submitHandler} 
            className="create-meeting-form"
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}
          >
            {submitError && (
              <Alert severity="error" className="create-meeting-alert">
                {submitError}
              </Alert>
            )}

            {/* Topic */}
            <TextField
              className="create-meeting-textfield"
              label="Meeting Topic" 
              placeholder="e.g., Weekly Team Standup"
              value={formData.topic} 
              onChange={e => changeHandler('topic', e.target.value)}
              error={Boolean(errors.topic)} 
              helperText={errors.topic || `${formData.topic.length}/128 characters`}
              disabled={isProcessing} 
              required 
              fullWidth
            />

            {/* Description Field - Added */}
            <TextField
              className="create-meeting-textfield"
              label="Description" 
              placeholder="e.g., Discuss project updates, blockers, and next steps"
              value={formData.description} 
              onChange={e => changeHandler('description', e.target.value)}
              error={Boolean(errors.description)} 
              helperText={errors.description || `${formData.description.length}/255 characters`}
              disabled={isProcessing} 
              fullWidth
              multiline
              rows={3}
              sx={{ '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
            />

            {/* Date & Duration */}
            <TextField
              className="create-meeting-textfield"
              label="Start Date & Time" 
              type="datetime-local"
              value={formData.startdatetime} 
              onChange={e => changeHandler('startdatetime', e.target.value)}
              error={Boolean(errors.startdatetime)} 
              helperText={errors.startdatetime || 'Must be at least 15 minutes from now'}
              disabled={isProcessing} 
              required 
              fullWidth 
              InputLabelProps={{ shrink: true }} 
              inputProps={{ min: minDateTime() }}
            />
            
            <FormControl fullWidth error={Boolean(errors.duration)} className="create-meeting-select">
              <InputLabel>Duration</InputLabel>
              <Select 
                value={formData.duration} 
                onChange={e => changeHandler('duration', e.target.value)} 
                disabled={isProcessing}
              >
                {DURATION_OPTIONS.map(opt => 
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                )}
              </Select>
              {errors.duration && (
                <Typography variant="caption" color="error">{errors.duration}</Typography>
              )}
            </FormControl>

            {/* Timezone */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Time Zone
              </Typography>
              <Chip 
                label={tzDisplay(formData.timezone)} 
                variant="outlined" 
                className="create-meeting-timezone-chip"
                sx={{ mb: 1 }} 
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Automatically detected from your system settings
              </Typography>
            </Box>

            <Alert severity="info" variant="outlined" className="create-meeting-alert">
              <Typography variant="body2">
                {isEdit 
                  ? 'Changes will be automatically synced with your Zoom meeting.' 
                  : 'Meeting link and ID will be generated after creation.'
                }
              </Typography>
            </Alert>

            <Box sx={{ flex: 1 }} />
          </Box>

          {/* Actions */}
          <Box className="create-meeting-actions" sx={{ display: 'flex', gap: 2 }}>
            <Button 
              onClick={closeHandler} 
              disabled={isProcessing} 
              variant="outlined" 
              fullWidth 
              className="create-meeting-secondary-btn"
              sx={{ py: 1.5, fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              type="submit" 
              variant="contained" 
              disabled={isProcessing} 
              fullWidth
              className="create-meeting-primary-btn"
              startIcon={isProcessing ? 
                <CircularProgress size={18} color="inherit" /> : 
                (isEdit ? <EditIcon /> : <VideoCallIcon />)
              }
              sx={{ py: 1.5, fontWeight: 600 }}
              onClick={submitHandler}
            >
              {isProcessing ? 
                (isEdit ? 'Updating...' : 'Creating...') : 
                (isEdit ? 'Update Meeting' : 'Create Meeting')
              }
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}